<template>
  <layout v-if="isAuthenticated">
    <template #header-left>
      <header-logo>Jarvis</header-logo>
    </template>
    <template #header-right>
      <input
        v-once
        :value="searchText"
        @input="setSearchText"
        type="search"
        placeholder="Claim #, Order #, Email, Name"
      />
      <profile :name="fullName" @logout="logout" :hasActions="true" />
    </template>
    <template #filters>
      <filters />
    </template>
    <template #sidebar>
      <sidebar-list>
        <sidebar-list-item
          :active="currentClaimStatus === CLAIM_STATUS.CUSTOMER_CARE_OPEN"
          @select="onUpdateClaimStatus(CLAIM_STATUS.CUSTOMER_CARE_OPEN)"
        >
          Support Requests ({{ getSupportRequestsNum() }})
        </sidebar-list-item>
        <sidebar-list-item
          :active="currentClaimStatus === CLAIM_STATUS.CUSTOMER_CARE_RESOLVED"
          @select="onUpdateClaimStatus(CLAIM_STATUS.CUSTOMER_CARE_RESOLVED)"
        >
          New Tickets ({{ getNewTicketsNum() }})
        </sidebar-list-item>
        <sidebar-list-item
          :active="currentClaimStatus === CLAIM_STATUS.QA_OPEN"
          @select="onUpdateClaimStatus(CLAIM_STATUS.QA_OPEN)"
        >
          Open Claims ({{ getOpenClaimsNum() }})
        </sidebar-list-item>
        <sidebar-list-item
          :active="isStatusPending"
          @select="onUpdateClaimStatus(CLAIM_STATUS.QA_PENDING_ALL)"
        >
          Pending Claims ({{ getPendingClaimsNum() }})
        </sidebar-list-item>
      </sidebar-list>
      <sidebar-list>
        <sidebar-list-item
          :active="currentClaimStatus === CLAIM_STATUS.QA_CLOSED"
          @select="onUpdateClaimStatus(CLAIM_STATUS.QA_CLOSED)"
        >
          Archived ({{ getArchivedClaimsNum() }})
        </sidebar-list-item>
      </sidebar-list>
    </template>
    <template #main>
      <div v-if="displayOpenClaimsActions" class="claim-actions">
        <div class="toggles-wrapper">
          <button
            v-for="(toggle, index) of openClaimsToggleValues"
            :key="`open_claim_toggle_${index}`"
            class="toggle"
            :class="{ active: currentClaimStatus === toggle.status }"
            @click="onUpdateClaimStatus(toggle.status)"
          >
            {{ toggle.label }} ({{ toggle.claimCount }})
          </button>
        </div>
        <button
          class="right"
          :disabled="loading || !canSubmitBatch"
          @click="toggleModal('submitBatch')"
        >
          Submit Batch
        </button>
      </div>
      <div v-if="displayPendingClaimsActions" class="claim-actions">
        <div v-if="loadingBatchesError">No Batch data</div>
        <template v-else>
          <select v-model="batchCode">
            <option value="">All</option>
            <option v-for="batchDetail of openBatchDetails" :key="batchDetail.universalId">
              {{ batchDetail.code }}
            </option>
          </select>
          <select class="claimStatusSelect" v-model="currentClaimStatus">
            <option v-for="status of PENDING_STATUSES" :value="status" :key="status">
              {{ CLAIM_STATUS_LABEL_MAP[status] }} ({{
                status === CLAIM_STATUS.QA_PENDING_ALL
                  ? getPendingClaimsNum()
                  : getStatusTotal(status)
              }})
            </option>
          </select>
        </template>
        <div class="button-groups right">
          <button class="white" :disabled="loading" @click="toggleModal('uploadFile')">
            Upload Batch
          </button>
          <button
            class="white"
            :disabled="loading || !canReSubmitBatch"
            @click="toggleModal('submitBatch')"
          >
            Re-Submit Batch
          </button>
          <button
            class="white"
            :disabled="loading || !canReSubmitBatch"
            @click="toggleModal('closeBatch')"
          >
            Close Batch
          </button>
        </div>
      </div>
      <claims-list v-if="loading">
        Loading claims data...
      </claims-list>
      <claims-list v-else>
        <div v-if="loadingClaimsError">No claim data</div>
        <claim-list-item
          v-else
          v-for="claim in claims"
          :key="claim.id"
          :claim="claim"
          @update="onUpdateClaim"
          @save-note="onSaveNote"
        />
      </claims-list>
    </template>
  </layout>
  <modal-upload-file
    v-if="showModal.uploadFile"
    @close="toggleModal('uploadFile')"
    @complete="onUploadBatchCompleted"
  />
  <modal-submit-batch
    v-if="showModal.submitBatch"
    :batchCode="batchCode"
    :supplierId="supplierId"
    @close="toggleModal('submitBatch')"
    @complete="onBatchSubmissionCompleted"
  />
  <modal-close-batch
    v-if="showModal.closeBatch"
    :batchCode="batchCode"
    :supplierId="supplierId"
    @close="toggleModal('closeBatch')"
    @complete="fetchBatchDetailsBySupplier(true)"
  />
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, provide, reactive, watch } from 'vue';
import { debounce, has } from 'lodash';
import ClaimListItem from '@/components/ClaimListItem.vue';
import ClaimsList from '@/components/ClaimsList.vue';
import Filters from '@/views/Filters.vue';
import HeaderLogo from '@/components/HeaderLogo.vue';
import Layout from '@/components/Layout.vue';
import ModalCloseBatch from '@/views/ModalCloseBatch.vue';
import ModalSubmitBatch from '@/views/ModalSubmitBatch.vue';
import ModalUploadFile from '@/views/ModalUploadFile.vue';
import Profile from '@/components/Profile.vue';
import SidebarList from '@/components/SidebarList.vue';
import SidebarListItem from '@/components/SidebarListItem.vue';

import { useAuth, useBatch, useClaims, useToast } from '@/hooks';
import {
  defaultFiltersValue,
  CLAIM_STATUS,
  OPEN_CLAIMS_TOGGLE,
  CLAIM_STATUS_LABEL_MAP,
  PENDING_STATUSES,
} from '@/data';
import { IBatchDetail, IClaim, IClaimNote, IClaimNoteOrReply, IClaimNoteReply } from '@/types';
import { Filter, FILTER_KEY } from '@/hooks/useFilter';
import { useRoute, useRouter } from 'vue-router';
import { NoteAdd } from '@/classes/note/noteAdd';
import { ReplyAdd } from '@/classes/note/replyAdd';
import { NoteUpdate } from '@/classes/note/noteUpdate';
import { ReplyUpdate } from '@/classes/note/replyUpdate';

export default defineComponent({
  name: 'Home',
  components: {
    ClaimListItem,
    ClaimsList,
    Filters,
    HeaderLogo,
    Layout,
    ModalCloseBatch,
    ModalSubmitBatch,
    ModalUploadFile,
    Profile,
    SidebarList,
    SidebarListItem,
  },
  setup() {
    const _router = useRouter();
    const _route = useRoute();

    const _filter: Filter = Filter.useFilter(_route.query);

    provide(FILTER_KEY, _filter);

    const { fullName, isAuthenticated, logout } = useAuth();
    const {
      loading: loadingClaims,
      claims,
      error: loadingClaimsError,
      statuses,
      getSupportRequestsNum,
      getNewTicketsNum,
      getStatusTotal,
      getOpenClaimsNum,
      getPendingClaimsNum,
      getArchivedClaimsNum,
      updateClaimLocal,
      updateClaimNotesLocal,
      getClaimsStatusByFilter,
      getClaims,
    } = useClaims();
    const {
      loading: loadingBatches,
      error: loadingBatchesError,
      batchDetails,
      getBatchDetailsBySupplier,
      resetBatchDetails,
    } = useBatch();
    const { toggleToast } = useToast();

    const loading = computed<boolean>(() => loadingClaims.value || loadingBatches.value);
    const supplierId = computed<number | undefined>(
      () => Number(_filter.filter.supplier) || undefined
    );
    const showModal = reactive<Record<string, boolean>>({
      uploadFile: false,
      submitBatch: false,
      closeBatch: false,
    });
    const currentClaimStatus = computed<string>({
      get: () => _filter.filter.status,
      set: (val: string) => {
        _filter.filter.status = val;
      },
    });
    const searchText = computed<string>(() => _filter.filter.searchText || '');
    const batchCode = computed<string>({
      get: () => _filter.filter.batchCode || '',
      set: (val: string) => {
        _filter.filter.batchCode = val;
      },
    });

    const openClaimsToggleValues = computed(() => {
      return OPEN_CLAIMS_TOGGLE.map((toggleItem: { status: string; label: string }) => {
        return {
          ...toggleItem,
          claimCount: getStatusTotal(toggleItem.status),
        };
      });
    });
    const displayOpenClaimsActions = computed<boolean>(
      () =>
        currentClaimStatus.value === CLAIM_STATUS.QA_OPEN ||
        currentClaimStatus.value === CLAIM_STATUS.QA_COMPLETE
    );
    const displayPendingClaimsActions = computed<boolean>(
      () => _filter.isStatusPending && !!_filter.filter.supplier
    );
    const displayClaimsActions = computed<boolean>(
      () => displayOpenClaimsActions.value || displayPendingClaimsActions.value
    );
    const canSubmitBatch = computed<boolean>(() =>
      Boolean(
        supplierId.value &&
          claims.value?.length > 0 &&
          currentClaimStatus.value === CLAIM_STATUS.QA_COMPLETE
      )
    );
    const canReSubmitBatch = computed<boolean>(() =>
      Boolean(
        supplierId.value &&
          claims.value?.length > 0 &&
          _filter.isStatusPending &&
          _filter.filter.batchCode
      )
    );
    const openBatchDetails = computed<IBatchDetail[]>(() =>
      batchDetails.value.filter((batchDetail: IBatchDetail) => !batchDetail.isClosed)
    );

    const fetchBatchDetailsBySupplier = (resetBatchCode: boolean) => {
      // After closing a batch, reset the batch select
      if (resetBatchCode) {
        _filter.filter.batchCode = '';
      }

      if (_filter.isStatusPending) {
        resetBatchDetails();

        if (supplierId.value) {
          getBatchDetailsBySupplier(supplierId.value);
        } else {
          toggleToast(
            true,
            'warning',
            'Please select a supplier first in order to get the pending claims batches'
          );
        }
      }
    };

    const _updateClaims = () => {
      if (_filter.isStatusPending) {
        const { getClaims: getClaimsByBatch } = useClaims(undefined, _filter.filter.batchCode);
        getClaimsByBatch(_filter.toLocationQuery());
      } else {
        getClaims(_filter.toLocationQuery());
      }
      getClaimsStatusByFilter(_filter.toLocationQuery());
    };

    /**
     * Handle changes to the "Claim #, Order #, Email, Name" input field.
     */
    const setSearchText = debounce((e: { target: HTMLInputElement }) => {
      // Update the filter with the new search text
      _filter.filter.searchText = e.target.value;
    }, 500);

    /**
     * Handle changes to the state filter (left nav; as well as incomplete/complete filter when "open claims" is selected)
     */
    const onUpdateClaimStatus = (newStatus: string) => {
      if (loading.value) return;

      _filter.filter.status = newStatus;
      _filter.filter.page = defaultFiltersValue.page;
    };

    const toggleModal = (modalName: string) => {
      showModal[modalName] = !showModal[modalName];
    };

    const onUpdateClaim = (payload: IClaim) => {
      if (!payload.id) return;

      // produce a warning message if finalAmount is empty
      if (has(payload, 'finalAmount') && payload.finalAmount?.toString() === '') {
        toggleToast(true, 'warning', 'Failed to save. Final Credit can not be empty.');
        return;
      }

      const { updateClaimSuccess, updateClaim } = useClaims(payload.id);

      updateClaim(payload).then(() => {
        if (!updateClaimSuccess.value) {
          _updateClaims();
        } else {
          updateClaimLocal(payload);
          getClaimsStatusByFilter(_filter.toLocationQuery());
        }
      });
    };

    function upsertClaimNoteReply(value: IClaimNoteReply, claimId: number) {
      if (value.id) {
        const replyUpdateApi = new ReplyUpdate(value.id);
        replyUpdateApi.updateReply(value).then(() => {
          if (replyUpdateApi.success.value) {
            updateClaimNotesLocal(value, claimId);
            toggleToast(true, 'success', `Successfully updated a reply in Claim ${claimId}`);
          } else {
            toggleToast(
              true,
              'error',
              `Could not update reply ${value.id} in Claim ${claimId}; ${replyUpdateApi.error.value}`
            );
          }
        });
      } else {
        const replyAddApi = new ReplyAdd();
        replyAddApi.addReply(value).then(() => {
          const newReply: IClaimNoteReply | undefined = replyAddApi.data.value;
          if (replyAddApi.success.value && newReply) {
            newReply.noteType = 'reply';
            updateClaimNotesLocal(newReply, claimId);
            toggleToast(true, 'success', `Successfully added a reply to Claim ${claimId}`);
          } else {
            toggleToast(
              true,
              'error',
              `Could not add reply in Claim ${claimId}; ${replyAddApi.error.value}`
            );
          }
        });
      }
    }

    function upsertClaimNote(value: IClaimNote, claimId: number) {
      console.log('attempting to add or update', value, claimId);
      if (value.id) {
        const noteUpdateApi = new NoteUpdate(value.id);
        noteUpdateApi.updateNote(value).then(() => {
          if (noteUpdateApi.success.value) {
            updateClaimNotesLocal(value, claimId);
            toggleToast(true, 'success', `Successfully updated a note in Claim ${claimId}`);
          } else {
            toggleToast(
              true,
              'error',
              `Could not update note ${value.id} in Claim ${claimId}; ${noteUpdateApi.error.value}`
            );
          }
        });
      } else {
        const noteAddApi = new NoteAdd();
        noteAddApi.addNote(value).then(() => {
          const newNote: IClaimNote | undefined = noteAddApi.data.value;
          if (noteAddApi.success.value && newNote) {
            newNote.noteType = 'note';
            updateClaimNotesLocal(newNote, claimId);
            toggleToast(true, 'success', `Successfully added a note to Claim ${claimId}`);
          } else {
            toggleToast(
              true,
              'error',
              `Could not add note in Claim ${claimId}; ${noteAddApi.error.value}`
            );
          }
        });
      }
    }

    function onSaveNote(value: IClaimNoteOrReply, claimId: number): void {
      if (value.noteType === 'reply') {
        upsertClaimNoteReply(value, claimId);
      } else if (value.noteType === 'note') {
        upsertClaimNote(value, claimId);
      }
    }

    const onBatchSubmissionCompleted = () => {
      _updateClaims();
    };

    const onUploadBatchCompleted = () => {
      _updateClaims();
    };

    const isStatusPending = computed<boolean>(() => _filter.isStatusPending);

    // watch supplier changes in route query and re-fetch batch details
    watch(
      () => [_filter.filter.supplier, _filter.isStatusPending],
      () => fetchBatchDetailsBySupplier(false)
    );

    watch(
      () => _filter.toLocationQuery(),
      () => {
        // When the filters change, update the querystring to allow user to reload the page
        _router.push({
          query: _filter.toLocationQuery(),
        });

        // And update the data
        _updateClaims();
      }
    );

    onMounted(() => {
      getClaims(_filter.toLocationQuery());
      getClaimsStatusByFilter(_filter.toLocationQuery());
      fetchBatchDetailsBySupplier(false);
    });

    return {
      CLAIM_STATUS,
      CLAIM_STATUS_LABEL_MAP,
      PENDING_STATUSES,
      openClaimsToggleValues,
      claims,
      onUpdateClaimStatus,
      showModal,
      toggleModal,
      fullName,
      isAuthenticated,
      logout,
      loading,
      loadingClaimsError,
      statuses,
      currentClaimStatus,
      searchText,
      setSearchText,
      onUpdateClaim,
      getSupportRequestsNum,
      getNewTicketsNum,
      getOpenClaimsNum,
      getPendingClaimsNum,
      getArchivedClaimsNum,
      displayClaimsActions,
      displayOpenClaimsActions,
      displayPendingClaimsActions,
      canSubmitBatch,
      canReSubmitBatch,
      loadingBatchesError,
      openBatchDetails,
      batchCode,
      supplierId,
      onSaveNote,
      onUploadBatchCompleted,
      onBatchSubmissionCompleted,
      fetchBatchDetailsBySupplier,
      isStatusPending,
      getStatusTotal,
    };
  },
});
</script>

<style lang="scss">
.claim-actions {
  display: flex;

  align-items: center;
  margin-bottom: var(--space-4);

  .toggles-wrapper {
    display: flex;
    align-items: center;

    button {
      margin-right: var(--space-2);
    }
  }

  select {
    width: auto !important;
  }

  .right {
    margin-left: auto;
  }

  .button-groups {
    button {
      margin-left: var(--space-2);
    }
  }

  .claimStatusSelect {
    margin-left: var(--space-2);
  }
}
</style>
